import React, {useEffect, useRef, useState} from 'react';
import {Alert, Button, Divider, Form, Input, Result, Typography} from 'antd';
import {portalLogin, sendPortalResetPasswordEmail} from './portalAuthService';
import {getRecaptchaToken} from './recaptcha';

/**
 * The Material Icons webfont is already loaded by the home stylesheet
 * (home/template/v2/css/app.css), and the other home pages use it the same way. Using it here keeps
 * the whole @mui bundle - around 2MB - off this public page for the sake of two icons.
 */
const MaterialIcon = ({name}) => <span className="material-icons" aria-hidden="true">{name}</span>;

const {Title, Paragraph, Text} = Typography;

const MODE_RESET = 'reset';
const MODE_LOGIN = 'login';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * Portal sign in for users who previously signed in through Cambridge Go.
 *
 * Resetting the password is the primary action: these accounts were authenticated by Cambridge Go, so
 * initially nobody has a Hotmaths password to sign in with. Signing in is offered as the secondary
 * action for anyone who has already set one.
 */
const PortalLogin = () => {
    // Someone arriving from a completed password reset already has a password, so open on the sign in
    // panel rather than the reset panel they have just finished with.
    const initialMode = new URLSearchParams(window.location.search).get('mode') === 'signin'
        ? MODE_LOGIN
        : MODE_RESET;

    const [mode, setMode] = useState(initialMode);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [resetEmailSentTo, setResetEmailSentTo] = useState(null);
    const [showUsername, setShowUsername] = useState(false);

    const [loginForm] = Form.useForm();
    const [resetForm] = Form.useForm();

    const resetEmailRef = useRef(null);
    const loginUsernameRef = useRef(null);
    const resultRef = useRef(null);
    // Focus is only moved in response to the user switching panel, never on first render - taking
    // focus on load would scroll past the rest of the page unprompted.
    const isFirstRender = useRef(true);

    const linkHash = new URLSearchParams(window.location.search).get('linkHash');

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // Move focus into the panel that just appeared so keyboard and screen reader users are not
        // left on a control that no longer exists.
        const target = resetEmailSentTo
            ? resultRef.current
            : (mode === MODE_RESET ? resetEmailRef.current : loginUsernameRef.current);

        if (target && target.focus) {
            target.focus();
        }
    }, [mode, resetEmailSentTo]);

    const switchMode = (nextMode) => {
        setMode(nextMode);
        setErrorMessage(null);
    };

    const handleLogin = async ({username, password}) => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const recaptchaToken = await getRecaptchaToken('portal_login');
            const result = await portalLogin(username, password, linkHash, recaptchaToken);
            if (result.success && result.redirectUrl) {
                // The brand domain completes the sign in and lands the user on their dashboard.
                window.location.assign(result.redirectUrl);
                return;
            }

            setErrorMessage(result.errorMessage || GENERIC_ERROR);
        } catch (error) {
            setErrorMessage(GENERIC_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async ({email, username}) => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const recaptchaToken = await getRecaptchaToken('portal_reset_password');
            const result = await sendPortalResetPasswordEmail(email, username, recaptchaToken);
            if (result.success) {
                setResetEmailSentTo(email);
                return;
            }

            // The server sets this when one email address has several active accounts and it needs a
            // username to tell them apart.
            if (result.usernameRequired) {
                setShowUsername(true);
            }

            setErrorMessage(result.errorMessage || GENERIC_ERROR);
        } catch (error) {
            setErrorMessage(GENERIC_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    if (resetEmailSentTo) {
        return (
            <div className="ch-portal-login"
                 ref={resultRef}
                 tabIndex={-1}
                 role="status"
                 aria-live="polite">
                <Result
                    status="success"
                    title="Check your email"
                    subTitle={
                        <span>
                            We have sent a link to set your password to <Text strong>{resetEmailSentTo}</Text>.
                            The link expires shortly, so please use it soon.
                        </span>
                    }
                    extra={
                        <Button
                            color="primary"
                            variant="outlined"
                            size="large"
                            icon={<MaterialIcon name="login"/>}
                            onClick={() => {
                                setResetEmailSentTo(null);
                                switchMode(MODE_LOGIN);
                            }}
                        >
                            Back to sign in
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div className="ch-portal-login">
            <Title level={2} className="ch-portal-login-title">Temporary login access</Title>

            <Alert
                className="ch-portal-login-notice"
                type="info"
                showIcon
                title=""
                description={
                    <span>
                        Cambridge GO is currently experiencing a disruption to service. This alternative login portal provides temporary access to your Cambridge digital resources until services are fully restored.
                    </span>
                }
            />

            {/* Always rendered, even when empty: a live region inserted at the same time as its content
                is not reliably announced. No role="alert" here on purpose - Ant Design already puts one
                on the Alert itself, and nesting two alert regions makes some screen readers announce
                the message twice. */}
            <div aria-live="assertive" aria-atomic="true">
                {errorMessage && (
                    <Alert
                        className="ch-portal-login-error"
                        type="error"
                        showIcon
                        title={errorMessage}
                    />
                )}
            </div>

            {mode === MODE_RESET ? (
                <div className="ch-portal-login-panel" key="reset-panel">
                    <Paragraph className="ch-portal-login-lead">
                        Enter the email address you normally use to log in to your Cambridge resources to receive a link to create a password to access this temporary portal.
                    </Paragraph>
                    <Form
                        form={resetForm}
                        layout="vertical"
                        requiredMark={false}
                        onFinish={handleResetPassword}
                        disabled={isLoading}
                    >
                        <Form.Item
                            label="Email address"
                            name="email"
                            rules={[
                                {required: true, message: 'Please enter your email address'},
                                {type: 'email', message: 'Please enter a valid email address'},
                            ]}
                        >
                            <Input ref={resetEmailRef} size="large" autoComplete="email"
                                   placeholder="you@example.com"/>
                        </Form.Item>

                        {showUsername && (
                            <Form.Item
                                label="Username"
                                name="username"
                                rules={[{required: true, message: 'Please enter your username'}]}
                                extra="More than one account uses this email address, so we need your username too."
                            >
                                <Input size="large" autoComplete="username"/>
                            </Form.Item>
                        )}

                        <Button
                            type="primary"
                            size="large"
                            htmlType="submit"
                            loading={isLoading}
                            icon={<MaterialIcon name="lock_reset"/>}
                            block
                        >
                            Email me a link to set my password
                        </Button>
                    </Form>

                    <Divider className="ch-portal-login-divider" plain>
                        Already set a password?
                    </Divider>
                    <Button
                        color="primary"
                        variant="outlined"
                        size="large"
                        onClick={() => switchMode(MODE_LOGIN)}
                        disabled={isLoading}
                        icon={<MaterialIcon name="login"/>}
                        block
                    >
                        Sign in
                    </Button>
                </div>
            ) : (
                <div className="ch-portal-login-panel" key="login-panel">
                    <Form
                        form={loginForm}
                        layout="vertical"
                        requiredMark={false}
                        onFinish={handleLogin}
                        disabled={isLoading}
                    >
                        <Form.Item
                            label="Username"
                            name="username"
                            rules={[{required: true, message: 'Please enter your username'}]}
                        >
                            <Input ref={loginUsernameRef} size="large" maxLength={100} autoComplete="username"/>
                        </Form.Item>

                        <Form.Item
                            label="Password"
                            name="password"
                            rules={[{required: true, message: 'Please enter your password'}]}
                        >
                            <Input.Password size="large" maxLength={60} autoComplete="current-password"/>
                        </Form.Item>

                        <Button
                            type="primary"
                            size="large"
                            htmlType="submit"
                            loading={isLoading}
                            icon={<MaterialIcon name="login"/>}
                            block
                        >
                            Sign in
                        </Button>
                    </Form>

                    <Paragraph className="ch-portal-login-switch">
                        Don&apos;t have a password yet, or forgotten it?{' '}
                        <Button type="link" onClick={() => switchMode(MODE_RESET)} disabled={isLoading}>
                            Set a new password
                        </Button>
                    </Paragraph>
                </div>
            )}
        </div>
    );
};

export default PortalLogin;
